import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

// Styles
import * as S from "./styles";

// Components
import KogModalFadeComponent from "@/main/components/modal/ModalFadeComponent";
import StarRatingVote from "@/main/components/shared/starRating/starRatingVote";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import KogTextAreaControlledNew from "@/main/components/shared/textArea/textArea";
import EvaluationSupplierGridParts from "./evaluationSupplierGridParts";
import Button from "@/main/components/shared/button/button";

// Utils
import { SuppliersToEvaluateResponse } from "@/main/models/response";
import { getTranslateByKey, isNullUndefined } from "@/main/services/utils";
import { firstElementList } from "@/main/services/utilsArray";
import { evaluationCompanyBuyerApi } from "@/main/api";

interface IModalEvaluationSupplierProps {
	isOpenModal: boolean;
	setIsOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
	closeModal: () => void;
	suppliers: SuppliersToEvaluateResponse[];
	guidProcurement: string;
	callbackActionAfterSaveSuccess?: (guidSupplier: string, guidEvaluationCompanyBuyer: string) => void;
}

const INITIAL_FORM_STATE_DEFAULT = {
	currentSupplier: null as SuppliersToEvaluateResponse | null,
	rating: 0,
	indexCurrentSupplier: 0,
	ratingDescription: "",
	ratingInvalid: false,
};
type IFormFieldsModalEvaluationSupplier = typeof INITIAL_FORM_STATE_DEFAULT;

function ModalEvaluationSupplier({
	suppliers,
	closeModal,
	guidProcurement,
	callbackActionAfterSaveSuccess,
}: IModalEvaluationSupplierProps) {
	// States
	const [modalOpenedFirstTime, setModalOpenedFirstTime] = useState<boolean>(false);
	const [isSaving, setIsSaving] = useState<boolean>(false);

	// Constants
	const PREFIX_ID_ELEMENTS_MODAL = "id-modalEvaluationSupplier-";

	// Hooks
	const methods = useForm<IFormFieldsModalEvaluationSupplier>({
		mode: "all",
		defaultValues: INITIAL_FORM_STATE_DEFAULT,
	});
	const { control, handleSubmit, setValue, watch, getValues } = methods;
	const { indexCurrentSupplier } = getValues();

	// Watchers
	const watchCurrentSupplier = watch("currentSupplier");
	const watchRating = watch("rating");
	const watchRatingInvalid = watch("ratingInvalid");

	// Functions
	function validateForm(data: IFormFieldsModalEvaluationSupplier) {
		if (isNullUndefined(data.rating) || data.rating === 0) {
			setValue("ratingInvalid", true);
			return false;
		}

		return true;
	}

	async function onIgnoreEvaluation() {
		nextSupplier();
	}

	async function onSaveEvaluation(data: IFormFieldsModalEvaluationSupplier) {
		if (!validateForm(data) || isSaving) return;
		setIsSaving(true);
		const paramGuidSupplier =
			!isNullUndefined(watchCurrentSupplier) && !isNullUndefined(watchCurrentSupplier.guidSupplier)
				? watchCurrentSupplier.guidSupplier
				: null;
		evaluationCompanyBuyerApi
			.saveSupplierEvaluation({
				ignoreSupplierEvaluation: false,
				evaluationDescription: !isNullUndefined(data.ratingDescription) ? data.ratingDescription : null,
				guidProcurement,
				evaluationScore: data.rating,
				guidSupplier: paramGuidSupplier,
			})
			.then((response) => {
				if (
					!isNullUndefined(response.data.result) &&
					!isNullUndefined(response.data.result.guidEvaluationCompanyBuyer) &&
					response.data.result.guidEvaluationCompanyBuyer !== ""
				) {
					if (!isNullUndefined(callbackActionAfterSaveSuccess)) {
						callbackActionAfterSaveSuccess(String(paramGuidSupplier), response.data.result.guidEvaluationCompanyBuyer);
					}
					toast.success(getTranslateByKey("information.supplierEvaluationSuccessfullyCompleted"));
					nextSupplier();
				}
			})
			.finally(() => {
				setIsSaving(false);
			});
	}

	function setRating(ratingVote: number) {
		setValue("rating", ratingVote);
		setValue("ratingInvalid", false);
	}

	function nextSupplier() {
		if (!isLastSupplier()) {
			const indexNextSuppler = indexCurrentSupplier + 1;
			const nextSupplier = suppliers[indexNextSuppler];
			setValue("indexCurrentSupplier", indexNextSuppler);
			setValue("currentSupplier", nextSupplier);

			// Clean fields
			setValue("ratingDescription", "");
			setRating(0);
		} else closeModal();
	}

	function isLastSupplier() {
		const indexLastSupplier = suppliers.length - 1;
		const isLastSupplier = indexLastSupplier === indexCurrentSupplier;
		return isLastSupplier;
	}

	function setFirstSupplieToEvaluate() {
		if (!isNullUndefined(suppliers) && suppliers.length > 0 && !modalOpenedFirstTime) {
			setValue("currentSupplier", firstElementList(suppliers));
			setModalOpenedFirstTime(true);
		}
	}

	useEffect(() => setFirstSupplieToEvaluate(), [suppliers]);

	if (isNullUndefined(watchCurrentSupplier)) {
		return <div></div>;
	}

	return (
		<form onSubmit={handleSubmit(onSaveEvaluation)} noValidate>
			<S.ContainerModal>
				<h6>
					{getTranslateByKey("information.evaluateYourPurchaseSupplier").replace(
						"{0}",
						watchCurrentSupplier.supplierName,
					)}
				</h6>
				<S.ContainerContentEvaluate>
					<S.ContainerMessage>
						<span style={{ marginBottom: 10 }}>{getTranslateByKey("information.evaluateSupplierMessage1")}</span>
						<span>{getTranslateByKey("information.evaluateSupplierMessage2")}</span>
					</S.ContainerMessage>

					<div className="d-flex flex-column align-items-center">
						<StarRatingVote
							readonly={false}
							onChangeRating={(rating: number) => {
								setRating(rating);
							}}
							size={38}
							rating={watchRating}
						/>
						{watchRatingInvalid && (
							<S.RatingLabelInvalid>{getTranslateByKey("information.evaluationIsRequired")}</S.RatingLabelInvalid>
						)}
					</div>

					<S.ContainerTextDescription>
						<S.TextDescription>{getTranslateByKey("information.evaluationPurchasingExperience")}</S.TextDescription>
						<KogTextAreaControlledNew
							inputProps={{ maxLength: 300 }}
							id={`${PREFIX_ID_ELEMENTS_MODAL}text-rating-description`}
							name="ratingDescription"
							control={control}
							rulesCustom={{
								required: false,
							}}
							label={getTranslateByKey("information.description")}
						/>
					</S.ContainerTextDescription>

					<EvaluationSupplierGridParts
						guidProcurement={guidProcurement}
						guidSupplier={
							!isNullUndefined(watchCurrentSupplier) && !isNullUndefined(watchCurrentSupplier.guidSupplier)
								? watchCurrentSupplier.guidSupplier
								: null
						}
					/>
				</S.ContainerContentEvaluate>
				<S.AreaButtons>
					<SecondaryButton
						id={`${PREFIX_ID_ELEMENTS_MODAL}btn-ignore-evaluation`}
						isLoading={false}
						onClick={onIgnoreEvaluation}
					>
						{getTranslateByKey("information.evaluationIgnore")}
					</SecondaryButton>
					<Button
						type="submit"
						isLoading={isSaving}
						style={{ marginLeft: "unset" }}
						id={`${PREFIX_ID_ELEMENTS_MODAL}btn-save-evaluation`}
					>
						{getTranslateByKey("information.evaluationSave")}
					</Button>
				</S.AreaButtons>
			</S.ContainerModal>
		</form>
	);
}

function WrapperModalEvaluationSupplier(props: IModalEvaluationSupplierProps) {
	return (
		<KogModalFadeComponent open={props.isOpenModal} setOpen={props.setIsOpenModal}>
			<ModalEvaluationSupplier {...props} />
		</KogModalFadeComponent>
	);
}

export default WrapperModalEvaluationSupplier;
